import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export type StylistSelectionMode = 'any' | 'specific';

interface BookingDraftState {
  shopId: string | null;
  // A solo (tenantless) stylist booking — shopId stays null, stylistId is that stylist's own account
  // id (not a Staff row), and every screen in this flow uses the ownerUserId-scoped endpoints
  // instead of tenant-scoped ones. See stylist/[id].tsx's independent-stylist branch for the entry
  // point, and bookForCustomer's own tenantId-optional handling on the backend.
  isIndependentStylist: boolean;
  serviceIds: string[];
  stylistMode: StylistSelectionMode;
  stylistId: string | null;
  date: string | null; // yyyy-mm-dd
  time: string | null; // "3:30 PM"
  couponCode: string | null;
  confirmedAppointmentIds: string[];
}

const initialState: BookingDraftState = {
  shopId: null,
  isIndependentStylist: false,
  serviceIds: [],
  stylistMode: 'any',
  stylistId: null,
  date: null,
  time: null,
  couponCode: null,
  confirmedAppointmentIds: [],
};

const bookingDraftSlice = createSlice({
  name: 'bookingDraft',
  initialState,
  reducers: {
    startBooking(state, action: PayloadAction<{ shopId: string | null; stylistId?: string; isIndependentStylist?: boolean }>) {
      Object.assign(state, initialState);
      state.shopId = action.payload.shopId;
      state.isIndependentStylist = !!action.payload.isIndependentStylist;
      if (action.payload.stylistId) {
        state.stylistMode = 'specific';
        state.stylistId = action.payload.stylistId;
      }
    },
    toggleService(state, action: PayloadAction<string>) {
      state.serviceIds = state.serviceIds.includes(action.payload)
        ? state.serviceIds.filter((id) => id !== action.payload)
        : [...state.serviceIds, action.payload];
    },
    setStylistMode(state, action: PayloadAction<StylistSelectionMode>) {
      state.stylistMode = action.payload;
      if (action.payload === 'any') state.stylistId = null;
    },
    setStylist(state, action: PayloadAction<string>) {
      state.stylistId = action.payload;
      state.stylistMode = 'specific';
    },
    setDate(state, action: PayloadAction<string>) {
      state.date = action.payload;
      state.time = null;
    },
    setTime(state, action: PayloadAction<string>) {
      state.time = action.payload;
    },
    applyCoupon(state, action: PayloadAction<string | null>) {
      state.couponCode = action.payload;
    },
    confirmBooking(state, action: PayloadAction<string[]>) {
      state.confirmedAppointmentIds = action.payload;
    },
    resetBookingDraft() {
      return initialState;
    },
  },
});

export const {
  startBooking,
  toggleService,
  setStylistMode,
  setStylist,
  setDate,
  setTime,
  applyCoupon,
  confirmBooking,
  resetBookingDraft,
} = bookingDraftSlice.actions;
export default bookingDraftSlice.reducer;
